import api from "@/lib/api";

const CHUNK = 4 * 1024 * 1024; // 4MB chunks to stay under proxy body limits
const CHUNK_TIMEOUT = 120000;
const COMPLETE_TIMEOUT = 90000;
const POLL_MS = 2000;
const PROCESS_DEADLINE_MS = 20 * 60 * 1000;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// Shaped like an axios error so callers' `e.response?.data?.detail` handling shows the message.
const uploadError = (msg) => Object.assign(new Error(msg), { response: { data: { detail: msg } } });

// Sends every chunk (with retries for transient failures); progress spans 0..span %.
async function sendChunks(file, onProgress, span) {
  const { data: init } = await api.post("/uploads/chunk/init", {});
  const uploadId = init.upload_id;
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const buf = await file.slice(i * CHUNK, (i + 1) * CHUNK).arrayBuffer();
    for (let attempt = 0; ; attempt++) {
      try {
        await api.put(`/uploads/chunk/${uploadId}/${i}`, buf, {
          headers: { "Content-Type": "application/octet-stream" }, timeout: CHUNK_TIMEOUT,
        });
        break;
      } catch (e) {
        const st = e.response?.status;
        if (attempt >= 2 || (st && st < 500)) throw e;
        await sleep(1500 * (attempt + 1));
      }
    }
    onProgress?.(Math.round(((i + 1) / total) * span));
  }
  return uploadId;
}

// Uploads a video File; resolves { id, url, size } only once the server has finished
// processing + storing it. 100% is reported only on real completion.
export async function uploadReferenceVideo(file, onProgress) {
  const uploadId = await sendChunks(file, onProgress, 90);
  const { data } = await api.post(`/uploads/chunk/${uploadId}/complete`, {
    filename: file.name || "video.mp4",
    content_type: file.type || "video/mp4",
    origin_url: window.location.origin,
  }, { timeout: COMPLETE_TIMEOUT });
  if (data.status !== "processing") { onProgress?.(100); return data; }
  const deadline = Date.now() + PROCESS_DEADLINE_MS;
  let pct = 90;
  while (Date.now() < deadline) {
    await sleep(POLL_MS);
    let st;
    try { ({ data: st } = await api.get(`/uploads/chunk/${uploadId}/status`, { timeout: 20000 })); }
    catch (e) { if (e.response?.status === 404) throw uploadError("Upload session was lost. Please try again."); continue; }
    if (st.status === "ready") { onProgress?.(100); return st; }
    if (st.status === "failed") throw uploadError(st.error || "Video processing failed. Please try again.");
    if (pct < 99) { pct += 1; onProgress?.(pct); }
  }
  throw uploadError("Video processing is taking too long. Please try again.");
}

// Uploads any raw asset (audio/video/image) in chunks without server-side video
// re-encoding; returns { id, url, size }. Served via /api/public/asset/{id}.
export async function uploadAsset(file, onProgress) {
  const uploadId = await sendChunks(file, onProgress, 95);
  const { data } = await api.post(`/uploads/chunk/${uploadId}/complete`, {
    kind: "asset",
    filename: file.name || "asset.bin",
    content_type: file.type || "application/octet-stream",
    origin_url: window.location.origin,
  }, { timeout: COMPLETE_TIMEOUT });
  onProgress?.(100);
  return data;
}

// Reads a File as a data URL (base64).
export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// Detects orientation + duration of a video file or URL.
export const probeVideo = (src) =>
  new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.onloadedmetadata = () => {
      const w = v.videoWidth, h = v.videoHeight;
      resolve({ orientation: orientationOf(w, h), duration: v.duration || 0, w, h });
    };
    v.onerror = () => resolve({ orientation: "unknown", duration: 0, w: 0, h: 0 });
    v.src = src;
  });

// Detects orientation of an image data URL / URL.
export const probeImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ orientation: orientationOf(img.naturalWidth, img.naturalHeight), w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ orientation: "unknown", w: 0, h: 0 });
    img.src = src;
  });

export function orientationOf(w, h) {
  if (!w || !h) return "unknown";
  if (w > h * 1.15) return "landscape";
  if (h > w * 1.15) return "portrait";
  return "square";
}
